/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOriginalCopy } from '@huse/previous-value';
import { usePrevious, useFunctionAsState } from 'utils/hooks';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import ListRow from './ListRow';
import DroppableBody from './DroppableBody';
import BootstrapTable from 'react-bootstrap/Table';
import HeaderRow from './HeaderRow';
import moment from 'moment';
import { getSortFunction } from './utils';
import DroppableBodyContainerProvider from './DroppableBodyContainerProvider';
import CustomScrollbarsVirtualList, { ScrollDataContext } from './CustomScrollbarsVirtualList';
import NoDataMessage from './NoDataMessage';
import _ from 'lodash';
import T from 'i18n';
import equal from 'react-fast-compare';
import { isObject } from 'utils/object';
import { isEmpty } from 'utils/objects';
import { useDispatch } from 'react-redux';
import { updateDocument } from 'features/case/redux/updateDocument';
import { useFilteredAndSortedData } from 'features/case/redux/setFilteredAndSortedData';
import { updateDocumentMeta } from 'features/case/redux/updateDocumentMeta';
import { useSelector } from 'react-redux';
import {
  selectCurrentFolder,
  selectBundleTabPage,
  selectDataMapFailedRows,
  selectUpdateDocumentSortFeedback,
  selectParams,
} from 'common/selectors';

export const getStringValue = (row: any, column: any) => {
  const id = typeof column === 'object' ? column.id : column;
  const value =
    typeof column === 'object'
      ? typeof column.accessor === 'function'
        ? column.accessor(row)
        : row[id]
      : row[id];

  if (!value) return '';

  if (typeof value === 'object') return JSON.stringify(value);

  return value.toString();
};

type FilterProps = {
  data: Array<any>;
  filter: any;
  filterByColumns: Array<any>;
  isBundleTabPage: boolean;
  filterOperators?: Record<string, 'and' | 'or'>;
  filterOrder?: string[];
};

// TO-DO remove business logic - no filter by this by that shite

const filterByGlobalPageIdx = (row: any, goToGlobalPageIdx: any) => {
  if (goToGlobalPageIdx) {
    if (!goToGlobalPageIdx.includes('.') && !row['startPage'].includes('.')) {
      const startPage = parseInt(row['startPage'], 10);
      const endPage = parseInt(row['lastPage'], 10);
      const goToPage = parseInt(goToGlobalPageIdx, 10);
      return goToPage >= startPage && goToPage <= endPage;
    } else if (
      (!goToGlobalPageIdx.includes('.') && row['startPage'].includes('.')) ||
      (goToGlobalPageIdx.includes('.') && !row['startPage'].includes('.'))
    ) {
      return false;
    } else if (goToGlobalPageIdx.includes('.') && row['startPage'].includes('.')) {
      const goToGlobalPage = goToGlobalPageIdx.split('.');
      const startPageVal = row['startPage'].split('.');
      const endPageVal = row['lastPage'].split('.');
      const isStartPageFirstDigitAfterDecimalZero =
        startPageVal && startPageVal.length > 1 && startPageVal[1].startsWith('0');
      const isFilterTermFirstDigitAfterDecimalZero =
        goToGlobalPage && goToGlobalPage.length > 1 && goToGlobalPage[1].startsWith('0');
      if (goToGlobalPage.length === 2 && goToGlobalPage[0] === startPageVal[0]) {
        if (isStartPageFirstDigitAfterDecimalZero && isFilterTermFirstDigitAfterDecimalZero) {
          //condition to filter late late inserts - when startpage starts with e.g. 55.01
          const startPage = startPageVal[1].slice(1, startPageVal[1].length);
          const goToPage = goToGlobalPage[1].slice(1, goToGlobalPage[1].length);
          const endPage = endPageVal[1].slice(1, endPageVal[1].length);
          return goToPage >= startPage && goToPage <= endPage;
        } else if (
          !isStartPageFirstDigitAfterDecimalZero &&
          !isFilterTermFirstDigitAfterDecimalZero
        ) {
          const startPage = parseInt(startPageVal[1], 10);
          const endPage = parseInt(endPageVal[1], 10);
          const goToPage = parseInt(goToGlobalPage[1], 10);
          return goToPage >= startPage && goToPage <= endPage;
        }
      }
      return false;
    }
  }
  return true;
};

const filterByArray = (row: any, arr: any, rowKey: any) => {
  if (arr && Object.entries(arr).length > 0) {
    const array: string[] = [];
    for (const key in arr) {
      if (!arr[key]) continue;
      const item = arr[key].toLowerCase();
      array.push(item);
    }
    return (
      row[rowKey] &&
      array.find((item: any) =>
        row[rowKey] && Array.isArray(row[rowKey])
          ? row[rowKey].map((i: any) => i && i.toLowerCase()).includes(item)
          : row[rowKey].toLowerCase().includes(item),
      )
    );
  } else return true;
};

// Generic date range filter that accepts a date accessor function
const createDateRangeFilter = (dateAccessor: (row: any) => string | null, dateFormat?: string) => {
  return (row: any, dateFrom: any, dateTo: any) => {
    const dateValue = dateAccessor(row);
    let rowDate: Date | null = null;

    if (dateValue) {
      if (dateFormat) {
        // Use moment for specific format parsing
        rowDate = new Date(moment(dateValue, dateFormat).toDate());
      } else {
        // For ISO strings or when format is not specified, use native Date parsing
        rowDate = new Date(dateValue);
      }
    }

    const startDate = dateFrom ? new Date(dateFrom) : null;
    const endDate = dateTo ? new Date(dateTo) : null;

    // Set end date to end of day (23:59:59.999) to include the entire "To" date
    if (endDate) {
      endDate.setHours(23, 59, 59, 999);
    }

    if (startDate && endDate) {
      return rowDate ? rowDate >= startDate && rowDate <= endDate : false;
    } else if (startDate) {
      return rowDate ? rowDate >= startDate : false;
    } else if (endDate) {
      return rowDate ? rowDate <= endDate : false;
    }
    return true;
  };
};

// Specific filter implementations using the generic filter
const filterByDate = createDateRangeFilter((row) => row['docDate'], 'DD/MM/YYYY HH:mm:ss');

const filterByDateUploaded = createDateRangeFilter(
  (row) => row.createdBy?.date,
  // No format specified - assumes ISO format from backend
);

const filterByAnnotations = (row: any, withAnnotations: any, withoutAnnotations: any) => {
  if (withAnnotations) return row['hasAnnotations'];
  else if (withoutAnnotations) return !row['hasAnnotations'];
  else return true;
};

const filterByShared = (row: any, shared: any, notShared: any) => {
  if (shared) return row['shared'];
  else if (notShared) return !row['shared'];
  else return true;
};

const filterByVisibility = (row: any, publicFiles: any, privateFiles: any) => {
  if (publicFiles) return !row['private'];
  else if (privateFiles) return row['private'];
  else return true;
};

const filterByPublicHyperlinks = (
  row: any,
  withPublicHyperlinks: any,
  withoutPublicHyperlinks: any,
) => {
  if (withPublicHyperlinks) return row['hasPublicHyperlinks'];
  else if (withoutPublicHyperlinks) return !row['hasPublicHyperlinks'];
  else return true;
};

const filterByFlags = (row: any, withFlag: any, noFlag: any) => {
  if (withFlag) return row['flags'] && Array.isArray(row['flags']) && row['flags'].length > 0;
  else if (noFlag)
    return !row['flags'] || !Array.isArray(row['flags']) || row['flags'].length === 0;
  else return true;
};

const filterByClassifyType = (row: any, classifyTypeFilters: any) => {
  // Handle both array and object formats
  let filtersArray = [];
  if (Array.isArray(classifyTypeFilters)) {
    filtersArray = classifyTypeFilters;
  } else if (classifyTypeFilters && typeof classifyTypeFilters === 'object') {
    filtersArray = Object.values(classifyTypeFilters);
  }

  if (filtersArray && filtersArray.length > 0) {
    const rowClassifyType = row.classify?.type || '';
    return filtersArray.some((filter: string) =>
      rowClassifyType.toLowerCase().includes(filter.toLowerCase()),
    );
  }
  return true;
};

const filterByClassifySubtype = (row: any, classifySubtypeFilters: any) => {
  // Handle both array and object formats
  let filtersArray = [];
  if (Array.isArray(classifySubtypeFilters)) {
    filtersArray = classifySubtypeFilters;
  } else if (classifySubtypeFilters && typeof classifySubtypeFilters === 'object') {
    filtersArray = Object.values(classifySubtypeFilters);
  }

  if (filtersArray && filtersArray.length > 0) {
    const rowClassifySubtype = row.classify?.subtype || '';
    return filtersArray.some((filter: string) =>
      rowClassifySubtype.toLowerCase().includes(filter.toLowerCase()),
    );
  }
  return true;
};

const filterByMerged = (row: any, withMerged: any, notMerged: any) => {
  if (withMerged) return row['source'] === 'merge';
  else if (notMerged) return row['source'] !== 'merge';
  else return true;
};

const filterByDocType = (row: any, docTypeFilters: any) => {
  // Handle both array and object formats
  let filtersArray = [];
  if (Array.isArray(docTypeFilters)) {
    filtersArray = docTypeFilters;
  } else if (docTypeFilters && typeof docTypeFilters === 'object') {
    filtersArray = Object.values(docTypeFilters);
  }

  if (filtersArray && filtersArray.length > 0) {
    const rowDocType = row.type || '';
    return filtersArray.some((filter: string) => rowDocType.toLowerCase() === filter.toLowerCase());
  }
  return true;
};

const filterByPageCount = (row: any, operator: string, value: any) => {
  if (!operator || !value) return true;

  // Parse row.pageCount - handle both string and number types
  let rowPageCount: number;
  if (typeof row.pageCount === 'string') {
    rowPageCount = parseInt(row.pageCount, 10);
  } else if (typeof row.pageCount === 'number') {
    rowPageCount = row.pageCount;
  } else {
    // If pageCount doesn't exist or is invalid, exclude this row
    return false;
  }

  // Parse filter value - handle both string and number types
  let filterValue: number;
  if (typeof value === 'string') {
    filterValue = parseInt(value, 10);
  } else if (typeof value === 'number') {
    filterValue = value;
  } else {
    return false;
  }

  // If either value is NaN after parsing, exclude this row
  if (isNaN(rowPageCount) || isNaN(filterValue)) return false;

  switch (operator) {
    case '=':
      return rowPageCount === filterValue;
    case '>':
      return rowPageCount > filterValue;
    case '>=':
      return rowPageCount >= filterValue;
    case '<':
      return rowPageCount < filterValue;
    case '<=':
      return rowPageCount <= filterValue;
    default:
      return true;
  }
};

export const filterByTabAndPage = (row: any, filterVal: any, isBundleTabPage: boolean) => {
  const pageNo = filterVal.length > 2 ? parseInt(filterVal[2], 10) : 1;
  if (getStringValue(row, 'tab').toLowerCase() === filterVal[1].toLowerCase()) {
    if (isBundleTabPage) {
      if (getStringValue(row, 'globalPagePrefix').toLowerCase() === filterVal[0].toLowerCase()) {
        return pageNo >= 1 && pageNo <= parseInt(row['pageCount'], 10);
      } else return false;
    }
    return pageNo >= 1 && pageNo <= parseInt(row['pageCount'], 10);
  } else return false;
};

export const filterByLocalPage = (row: any, filterVal: any) => {
  const pageNo = parseInt(filterVal[1], 10);
  if (getStringValue(row, 'id').toLowerCase().includes(filterVal[0].toLowerCase())) {
    return pageNo >= 1 && pageNo <= parseInt(row['pageCount'], 10);
  } else return false;
};

// 1. Update filterByGlobalPage method (around line 172)
export const filterByGlobalPage = (row: any, filterTerm: any) => {
  if (filterTerm) {
    // Check for the complex global page formats with dots (A9.99, A99.99, etc.)
    if (/^[a-zA-Z][A-Za-z0-9]*(\.\d{1,2})?$/.test(filterTerm) && !!row['globalPagePrefix']) {
      // Check if the filter term starts with the document's global page prefix
      const rowPrefix = row.globalPagePrefix.toLowerCase();

      // Case 1: Exact prefix match (like "G4.1" or "A99.99" matching a document with that prefix)
      if (filterTerm.toLowerCase() === rowPrefix) {
        return true;
      }

      // Case 2: Prefix + page number (like "G4.11" where "G4.1" is the prefix)
      if (filterTerm.toLowerCase().startsWith(rowPrefix)) {
        const valueAfterPrefix = filterTerm.toLowerCase().replace(rowPrefix, '');

        // If there's a value after the prefix, it should be a page number
        if (valueAfterPrefix && !isNaN(Number(valueAfterPrefix))) {
          return filterByGlobalPageIdx(row, valueAfterPrefix);
        }
      }

      // No match for this document
      return false;
    }

    // Legacy pattern (kept for backward compatibility)
    else if (/^[a-zA-Z]{1,2}[0-9.]{1,9}?$/.test(filterTerm) && !!row['globalPagePrefix']) {
      const index = filterTerm.toLowerCase().indexOf(row.globalPagePrefix.toLowerCase());
      if (index === 0) {
        const valueAfterPrefix = filterTerm
          .toLowerCase()
          .replace(row.globalPagePrefix.toLowerCase(), '');
        if (!isNaN(Number(valueAfterPrefix))) {
          return filterByGlobalPageIdx(row, valueAfterPrefix);
        } else return false;
      } else return false;
    }

    return false;
  }
  return true;
};

export const filterByTrialBundles = (row: any, arr: any) => {
  if (arr && Object.entries(arr).length > 0) {
    const array: string[] = [];
    for (const key in arr) {
      if (!arr[key]) continue;
      const item = arr[key];
      array.push(item);
    }

    return !!(
      row.bundleLocations.length > 0 &&
      row.bundleLocations.find((itm: any) => array.includes(itm.folderId))
    );
  } else return true;
};
export const filterByTeamBundles = (row: any, arr: any) => {
  if (arr && Object.entries(arr).length > 0) {
    const array: string[] = [];
    for (const key in arr) {
      if (!arr[key]) continue;
      const item = arr[key];
      array.push(item);
    }

    return !!(
      row.bundleLocations.length > 0 &&
      row.bundleLocations.find((itm: any) => array.includes(itm.folderId))
    );
  } else return true;
};

export const filterByPrivateBundles = (row: any, arr: any) => {
  if (arr && Object.entries(arr).length > 0) {
    const array: string[] = [];
    for (const key in arr) {
      if (!arr[key]) continue;
      const item = arr[key];
      array.push(item);
    }

    return !!(
      row.bundleLocations.length > 0 &&
      row.bundleLocations.find((itm: any) => array.includes(itm.folderId))
    );
  } else return true;
};

export const filterByFilterTerm = (
  row: any,
  filterByColumns: any,
  term: string,
  isBundleTabPage: boolean,
  forceTextSearchMode = false,
) => {
  // Return true for empty terms
  if (!term || term.trim() === '') return true;

  // Trim the term to remove any whitespace
  term = term.trim();

  // Get columns to search in
  const columns = filterByColumns ? filterByColumns : Object.keys(row);

  // Bypass special format checks for short text or partial words that don't match special formats
  const isSimpleTextSearch =
    // Single letters or short words (less than 3 chars)
    term.length < 3 ||
    // Basic words (for "Day", "Da", etc.) - standard text without special format chars
    /^[a-zA-Z\s]+$/.test(term) ||
    // Words with hyphen but not special format patterns
    (/^[a-zA-Z\s-]+$/.test(term) && !term.includes('/'));

  // For simple text searches, go directly to text search
  if (isSimpleTextSearch) {
    const termLower = term.toLowerCase();
    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      const isColumnObject = typeof column === 'object';
      if (!isColumnObject && !row.hasOwnProperty(column)) {
        // Special case for recipient
        if (column === 'recipient' && row.recipient === null && row.recipientsLists) {
          if (
            row.recipientsLists.some((recipient: string) =>
              recipient.toLowerCase().includes(termLower),
            )
          ) {
            return true;
          }
        }
        continue;
      }

      const value = getStringValue(row, column);
      if (value.toLowerCase().includes(termLower)) {
        return true;
      }
    }
    return false;
  }

  // If we are explicitly forcing text search, skip all special format checks
  if (forceTextSearchMode) {
    const termLower = term.toLowerCase();
    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      const isColumnObject = typeof column === 'object';
      if (!isColumnObject && !row.hasOwnProperty(column)) {
        // Special case for recipient
        if (column === 'recipient' && row.recipient === null && row.recipientsLists) {
          if (
            row.recipientsLists.some((recipient: string) =>
              recipient.toLowerCase().includes(termLower),
            )
          ) {
            return true;
          }
        }
        continue;
      }

      const value = getStringValue(row, column);
      if (value.toLowerCase().includes(termLower)) {
        return true;
      }
    }
    return false;
  }

  // SPECIAL FORMAT CHECKS - Only run these for terms that match special patterns

  // Format 1: Complex prefix with tab like "G4.1/2" or "A99.99/5" or "F1/5.1"
  if (/^[A-Za-z][A-Za-z0-9]*(\.\d{1,2})?\/\d+(\.\d+)?(?:\/\d+)?$/.test(term)) {
    const parts = term.split('/');
    const complexPrefix = parts[0]; // "G4.1" or "A99.99" or "F1"
    const tabNumber = parts[1]; // "2" or "5" or "5.1"
    const pageNumber = parts.length > 2 ? parseInt(parts[2], 10) : null;

    if (
      row.globalPagePrefix &&
      row.globalPagePrefix.toLowerCase() === complexPrefix.toLowerCase() &&
      getStringValue(row, 'tab').toLowerCase() === tabNumber.toLowerCase()
    ) {
      if (pageNumber !== null) {
        return pageNumber >= 1 && pageNumber <= parseInt(row.pageCount || '1', 10);
      }
      return true;
    }
    return false;
  }

  // Format 2: Simple prefix with tab like "E/3/5" or "E/3.5"
  if (/^[A-Za-z]\/\d+(\.\d+)?(?:\/\d+)?$/.test(term)) {
    const parts = term.split('/');
    const prefix = parts[0]; // E
    const tabNumber = parts[1]; // 3 or 3.5
    const pageNumber = parts.length > 2 ? parseInt(parts[2], 10) : null; // 5 or null

    if (
      row.globalPagePrefix &&
      row.globalPagePrefix.toLowerCase() === prefix.toLowerCase() &&
      getStringValue(row, 'tab').toLowerCase() === tabNumber.toLowerCase()
    ) {
      if (pageNumber !== null) {
        return pageNumber >= 1 && pageNumber <= parseInt(row.pageCount || '1', 10);
      }
      return true;
    }
    return false;
  }

  // Format 3: Global Page format like "E3", "B116", "A9.99", "A99.99"
  if (
    !forceTextSearchMode &&
    /^[a-zA-Z][A-Za-z0-9]*(\.\d{1,2})?$/.test(term) &&
    row.globalPagePrefix
  ) {
    if (isBundleTabPage) {
      return false;
    }

    const prefix = row.globalPagePrefix;
    if (term.toLowerCase().startsWith(prefix.toLowerCase())) {
      // For complex formats with dots
      if (term.includes('.')) {
        // This is a direct match like "A9.99" === "A9.99" (prefix match)
        if (term.toLowerCase() === prefix.toLowerCase()) {
          return true;
        }

        // This could be a page number after a pattern like "A9.991" (where A9.99 is the prefix)
        const valueAfterPrefix = term.toLowerCase().replace(prefix.toLowerCase(), '');
        if (valueAfterPrefix && !isNaN(Number(valueAfterPrefix))) {
          const pageNumber = parseInt(valueAfterPrefix, 10);
          const startPage = parseInt(row.startPage, 10);
          const lastPage = parseInt(row.lastPage, 10);
          return pageNumber >= startPage && pageNumber <= lastPage;
        }
      } else {
        // For simple formats without dots like "A1" or "E456"
        const pageNumber = parseInt(term.slice(prefix.length), 10);
        const startPage = parseInt(row.startPage, 10);
        const lastPage = parseInt(row.lastPage, 10);
        return pageNumber >= startPage && pageNumber <= lastPage;
      }
    }
    return false;
  }

  // Format 4: Trial View ID with page number like "TV-426/11"
  if (/^[Tt][Vv]-\d{2,}\/\d+$/.test(term)) {
    const parts = term.split('/');
    const trialViewId = parts[0]; // e.g., "TV-426"
    const pageNumber = parseInt(parts[1], 10);

    // Check both ID and name for this pattern
    const matchesId = row.id && row.id.toLowerCase().includes(trialViewId.toLowerCase());
    const matchesName = row.name && row.name.toLowerCase().includes(trialViewId.toLowerCase());

    if (matchesId || matchesName) {
      return pageNumber >= 1 && pageNumber <= parseInt(row.pageCount || '1', 10);
    }
    return false;
  }

  // Format 5: Ticket-style ID with page number like "TV-426-9885/5"
  if (/^[A-Za-z]+-\d+-\d+\/\d+$/.test(term)) {
    const parts = term.split('/');
    const ticketId = parts[0]; // e.g., "TV-426-9885"
    const pageNumber = parseInt(parts[1], 10);

    // Check both ID and name for this pattern
    const matchesName = row.name && row.name.toLowerCase().includes(ticketId.toLowerCase());
    const matchesId = row.id && row.id.toLowerCase().includes(ticketId.toLowerCase());

    if (matchesName || matchesId) {
      return pageNumber >= 1 && pageNumber <= parseInt(row.pageCount || '1', 10);
    }
    return false;
  }

  // Format 6: Basic ID/page pattern like 0137/6
  if (/^\d+\/\d+$/.test(term)) {
    const parts = term.split('/');
    const docId = parts[0];
    const pageNumber = parseInt(parts[1], 10);

    if (row.id && getStringValue(row, 'id').toLowerCase().includes(docId.toLowerCase())) {
      return pageNumber >= 1 && pageNumber <= parseInt(row.pageCount || '1', 10);
    }
    return false;
  }

  // STANDARD TEXT SEARCH - run for all non-matching patterns
  const termLower = term.toLowerCase();
  for (let i = 0; i < columns.length; i++) {
    const column = columns[i];
    const isColumnObject = typeof column === 'object';
    if (!isColumnObject && !row.hasOwnProperty(column)) {
      // Special case for recipient
      if (column === 'recipient' && row.recipient === null && row.recipientsLists) {
        if (
          row.recipientsLists.some((recipient: string) =>
            recipient.toLowerCase().includes(termLower),
          )
        ) {
          return true;
        }
      }
      continue;
    }

    const value = getStringValue(row, column);
    if (value.toLowerCase().includes(termLower)) {
      return true;
    }
  }

  return false;
};
// Helper: derive base key from instance key (e.g. authors__1 -> authors)
const getBaseKey = (key: string) => key.split('__')[0];

// Helper function to get filter function for a specific filter instance key
// Supports instance-specific values stored at filter[instanceKey]
const getFilterFunctionForKey = (instanceKey: string, filter: any) => {
  const baseKey = getBaseKey(instanceKey);
  // Get the filter instance data - should be stored at filter[instanceKey]
  const filterInstance = filter[instanceKey];

  // If no instance data found, return a function that matches everything
  if (!filterInstance) {
    return (row: any) => true;
  }

  return (row: any) => {
    switch (baseKey) {
      case 'trialBundles':
        return filterByTrialBundles(row, filterInstance.trialBundles || filterInstance);
      case 'teamBundles':
        return filterByTeamBundles(row, filterInstance.teamBundles || filterInstance);
      case 'privateBundles':
        return filterByPrivateBundles(row, filterInstance.privateBundles || filterInstance);
      case 'date':
        return filterByDate(row, filterInstance.dateFrom, filterInstance.dateTo);
      case 'dateUploaded':
        return filterByDateUploaded(
          row,
          filterInstance.dateUploadedFrom,
          filterInstance.dateUploadedTo,
        );
      case 'classifyType':
        return filterByClassifyType(row, filterInstance.classifyType || filterInstance);
      case 'classifySubtype':
        return filterByClassifySubtype(row, filterInstance.classifySubtype || filterInstance);
      case 'annotations':
        return filterByAnnotations(
          row,
          filterInstance.withAnnotations,
          filterInstance.withoutAnnotations,
        );
      case 'hyperlinks':
        return filterByPublicHyperlinks(
          row,
          filterInstance.withPublicHyperlinks,
          filterInstance.withoutPublicHyperlinks,
        );
      case 'visibility':
        return filterByVisibility(row, filterInstance.public, filterInstance.private);
      case 'flag':
        return filterByFlags(row, filterInstance.withFlag, filterInstance.noFlag);
      case 'merged':
        return filterByMerged(row, filterInstance.withMerged, filterInstance.notMerged);
      case 'authors':
        return filterByArray(row, filterInstance.authors || filterInstance, 'author');
      case 'recipients':
        return filterByArray(row, filterInstance.recipients || filterInstance, 'recipient');
      case 'docType':
        return filterByDocType(row, filterInstance.docType || filterInstance);
      case 'pageCount':
        return filterByPageCount(
          row,
          filterInstance.pageCountOperator,
          filterInstance.pageCountValue,
        );
      default:
        return true;
    }
  };
};

export const filterData = ({
  data,
  filter,
  filterByColumns,
  isBundleTabPage,
  filterOperators = {},
  filterOrder = [],
}: FilterProps) => {
  if (isEmpty(filter)) {
    return null;
  } else {
    const {
      term,
      goToGlobalPageIdx,
      authors = {},
      recipients = {},
      classifyType = [],
      classifySubtype = [],
      createdBy = {},
      tags = {},
      dateFrom,
      dateTo,
      dateUploadedFrom,
      dateUploadedTo,
      withAnnotations,
      withoutAnnotations,
      shared,
      notShared,
      public: publicFiles,
      private: privateFiles,
      withPublicHyperlinks,
      withoutPublicHyperlinks,
      withFlag,
      noFlag,
      withMerged,
      notMerged,
      trialBundles = {},
      teamBundles = {},
      privateBundles = {},
    } = filter;

    const normalizedTerm = term && typeof term === 'string' ? term.trim() : '';
    const isGlobalPageLikeTerm =
      !!normalizedTerm && /^[a-zA-Z][A-Za-z0-9]*(\.\d{1,2})?$/.test(normalizedTerm);

    // If the term looks like a global page reference (e.g. "E10", "B1980")
    // but does NOT match any document's globalPagePrefix, we should treat it
    // as a normal text search term instead of a global page reference.
    const hasMatchingGlobalPrefix =
      isGlobalPageLikeTerm &&
      data.some(
        (row) =>
          row &&
          row.globalPagePrefix &&
          normalizedTerm.toLowerCase().startsWith(row.globalPagePrefix.toLowerCase()),
      );

    const forceTextSearchMode =
      !!normalizedTerm && isGlobalPageLikeTerm && !hasMatchingGlobalPrefix;

    const hasOnlyTerm =
      term &&
      !goToGlobalPageIdx &&
      !Object.entries(authors).length &&
      !Object.entries(recipients).length &&
      !(Array.isArray(classifyType)
        ? classifyType.length
        : Object.entries(classifyType || {}).length) &&
      !(Array.isArray(classifySubtype)
        ? classifySubtype.length
        : Object.entries(classifySubtype || {}).length) &&
      !Object.entries(createdBy).length &&
      !dateFrom &&
      !dateTo &&
      !dateUploadedFrom &&
      !dateUploadedTo &&
      !withAnnotations &&
      !withoutAnnotations &&
      !shared &&
      !notShared &&
      !publicFiles &&
      !privateFiles &&
      !withPublicHyperlinks &&
      !withoutPublicHyperlinks &&
      !withFlag &&
      !noFlag &&
      !withMerged &&
      !notMerged &&
      !Object.entries(trialBundles).length &&
      !Object.entries(teamBundles).length &&
      !Object.entries(privateBundles).length &&
      !Object.entries(tags).length;
    // If filterOrder is provided, use instance-based AND/OR logic
    // New logic: filterOrder may contain instance keys with possible duplicates of same base key
    if (filterOrder && filterOrder.length > 0) {
      // First, apply term filtering if present
      let baseData = data;
      if (term) {
        baseData = data.filter((row) => {
          // Special case for global prefixes with dots (like "G4.1", "A9.99", "A99.99")
          if (
            !forceTextSearchMode &&
            /^[A-Za-z][A-Za-z0-9]*(\.\d{1,2})?$/.test(term) &&
            row.globalPagePrefix
          ) {
            if (row.globalPagePrefix.toLowerCase() === term.toLowerCase()) {
              return true;
            }
          }
          // For complex formats with slashes
          if (/^[A-Za-z][A-Za-z0-9]*(\.\d{1,2})?\/[\d.]+(?:\/\d+)?$/.test(term)) {
            const parts = term.split('/');
            const complexPrefix = parts[0];
            const tabNumber = parts[1];
            const pageNumber = parts.length > 2 ? parseInt(parts[2], 10) : null;
            if (
              row.globalPagePrefix &&
              row.globalPagePrefix.toLowerCase() === complexPrefix.toLowerCase() &&
              getStringValue(row, 'tab').toLowerCase() === tabNumber.toLowerCase()
            ) {
              if (pageNumber !== null) {
                return pageNumber >= 1 && pageNumber <= parseInt(row.pageCount || '1', 10);
              }
              return true;
            }
            return false;
          }

          return (
            filterByFilterTerm(row, filterByColumns, term, isBundleTabPage, forceTextSearchMode) ||
            (row && row.matches && row.matches.length > 0) ||
            (!forceTextSearchMode && filterByGlobalPage(row, term))
          );
        });
      }

      // Apply category filters with AND/OR logic
      let result = baseData;
      let orAccumulator: any[] = [];

      filterOrder.forEach((instanceKey, index) => {
        const operator = filterOperators[instanceKey] || 'and';
        const filterFn = getFilterFunctionForKey(instanceKey, filter);

        if (index === 0) {
          // First filter - always apply it
          result = result.filter(filterFn);
        } else if (operator === 'and') {
          // AND - apply to accumulated OR results if any, then narrow down result
          if (orAccumulator.length > 0) {
            // First apply OR accumulator
            result = [...new Set([...result, ...orAccumulator])];
            orAccumulator = [];
          }
          // Then apply AND filter
          result = result.filter(filterFn);
        } else if (operator === 'or') {
          // OR - accumulate matching rows
          const orResults = baseData.filter(filterFn);
          orAccumulator.push(...orResults);
        }
      });

      // Apply any remaining OR accumulator
      if (orAccumulator.length > 0) {
        result = [...new Set([...result, ...orAccumulator])];
      }

      // Apply remaining base filters not represented as instances (legacy support)
      result = result.filter((row) => {
        const baseFilters =
          filterByGlobalPageIdx(row, goToGlobalPageIdx) &&
          filterByArray(row.createdBy, createdBy, 'id') &&
          filterByArray(row, tags, 'tags') &&
          filterByDateUploaded(row, dateUploadedFrom, dateUploadedTo) &&
          filterByShared(row, shared, notShared);
        const entitiesIsInCategory =
          row.entities && isObject(row.entities)
            ? Object.keys(row.entities).reduce(
                (acc, key) =>
                  acc && (key === 'cost' ? true : filterByArray(row.entities, filter[key], key)),
                true,
              )
            : true;
        return baseFilters && entitiesIsInCategory;
      });
      return result;
    }

    // Original filtering logic (backwards compatible - no operators)
    const filteredData = data.filter((row) => {
      // Special case for global prefixes with dots (like "G4.1", "A9.99", "A99.99")
      if (
        term &&
        !forceTextSearchMode &&
        /^[A-Za-z][A-Za-z0-9]*(\.\d{1,2})?$/.test(term) &&
        row.globalPagePrefix
      ) {
        // Direct prefix match
        if (row.globalPagePrefix.toLowerCase() === term.toLowerCase()) {
          return true;
        }
      }
      // For complex formats with slashes like "A99.99/5/6" or with multi-decimal tabs like "F1/9.4.1.1"
      if (/^[A-Za-z][A-Za-z0-9]*(\.\d{1,2})?\/[\d.]+(?:\/\d+)?$/.test(term)) {
        const parts = term.split('/');
        const complexPrefix = parts[0]; // "G4.1" or "A99.99" or "F1"
        const tabNumber = parts[1]; // "2" or "5" or "5.1" or "999.444.111.111.111.111"
        const pageNumber = parts.length > 2 ? parseInt(parts[2], 10) : null;
        if (
          row.globalPagePrefix &&
          row.globalPagePrefix.toLowerCase() === complexPrefix.toLowerCase() &&
          getStringValue(row, 'tab').toLowerCase() === tabNumber.toLowerCase()
        ) {
          if (pageNumber !== null) {
            return pageNumber >= 1 && pageNumber <= parseInt(row.pageCount || '1', 10);
          }
          return true;
        }
        return false;
      }

      const isInGrid =
        filterByFilterTerm(row, filterByColumns, term, isBundleTabPage, forceTextSearchMode) ||
        (row && row.matches && row.matches.length > 0) ||
        (term && !forceTextSearchMode && filterByGlobalPage(row, term)); // Check by global page

      if (hasOnlyTerm) {
        return isInGrid;
      } else {
        const isInCategory =
          filterByGlobalPageIdx(row, goToGlobalPageIdx) &&
          filterByTrialBundles(row, filter.trialBundles) &&
          filterByTeamBundles(row, filter.teamBundles) &&
          filterByPrivateBundles(row, filter.privateBundles) &&
          filterByArray(row, authors, 'author') &&
          filterByArray(row, recipients, 'recipient') &&
          filterByArray(row.createdBy, createdBy, 'id') &&
          filterByArray(row, tags, 'tags') &&
          filterByDate(row, dateFrom, dateTo) &&
          filterByDateUploaded(row, dateUploadedFrom, dateUploadedTo) &&
          filterByAnnotations(row, withAnnotations, withoutAnnotations) &&
          filterByShared(row, shared, notShared) &&
          filterByVisibility(row, publicFiles, privateFiles) &&
          filterByPublicHyperlinks(row, withPublicHyperlinks, withoutPublicHyperlinks) &&
          filterByFlags(row, withFlag, noFlag) &&
          filterByMerged(row, withMerged, notMerged) &&
          filterByClassifyType(row, filter.classifyType) &&
          filterByClassifySubtype(row, filter.classifySubtype);

        const entitiesIsInCategoryAndGrid =
          row.entities && isObject(row.entities)
            ? Object.keys(row.entities).reduce(
                (acc, key) =>
                  acc && (key === 'cost' ? true : filterByArray(row.entities, filter[key], key)),
                true,
              )
            : true;

        return isInGrid && isInCategory && entitiesIsInCategoryAndGrid;
      }
    });

    // Fallback: if a term looks like a global page reference but produced no results,
    // re-run the filtering as plain text search (ignore all special global-page handling).
    if (
      filteredData.length === 0 &&
      normalizedTerm &&
      isGlobalPageLikeTerm &&
      !forceTextSearchMode
    ) {
      const fallbackFilteredData = data.filter((row) => {
        const isInGrid =
          filterByFilterTerm(row, filterByColumns, term, isBundleTabPage, true) ||
          (row && row.matches && row.matches.length > 0);

        if (hasOnlyTerm) {
          return isInGrid;
        }

        const isInCategory =
          filterByGlobalPageIdx(row, goToGlobalPageIdx) &&
          filterByTrialBundles(row, filter.trialBundles) &&
          filterByTeamBundles(row, filter.teamBundles) &&
          filterByPrivateBundles(row, filter.privateBundles) &&
          filterByArray(row, authors, 'author') &&
          filterByArray(row, recipients, 'recipient') &&
          filterByArray(row.createdBy, createdBy, 'id') &&
          filterByArray(row, tags, 'tags') &&
          filterByDate(row, dateFrom, dateTo) &&
          filterByDateUploaded(row, dateUploadedFrom, dateUploadedTo) &&
          filterByAnnotations(row, withAnnotations, withoutAnnotations) &&
          filterByShared(row, shared, notShared) &&
          filterByVisibility(row, publicFiles, privateFiles) &&
          filterByPublicHyperlinks(row, withPublicHyperlinks, withoutPublicHyperlinks) &&
          filterByFlags(row, withFlag, noFlag) &&
          filterByMerged(row, withMerged, notMerged) &&
          filterByClassifyType(row, filter.classifyType) &&
          filterByClassifySubtype(row, filter.classifySubtype);

        const entitiesIsInCategoryAndGrid =
          row.entities && isObject(row.entities)
            ? Object.keys(row.entities).reduce(
                (acc, key) =>
                  acc && (key === 'cost' ? true : filterByArray(row.entities, filter[key], key)),
                true,
              )
            : true;

        return isInGrid && isInCategory && entitiesIsInCategoryAndGrid;
      });
      return fallbackFilteredData;
    }
    return filteredData;
  }
};

type TableProps = {
  data: Array<any>;
  filter: any;
  filterByColumns: Array<any>;
  onDoubleClick: (row: any) => void;
  columns: Array<any>;
  sortMode: boolean;
  editMode?: boolean;
  additionalDragInfo?: object;
  onRowSelected: (selectedRows: Array<any>) => void;
  updateSort: (index: any, ids: any, singleDroppedDocId: string, hasFolderFieldId: boolean) => void;
  onFilterChanged?: (filteredRows: Array<any> | null, value: string | null) => void;
  rowHeight?: number;
  readOnly?: boolean;
  gridList?: any;
  defaultMessage?: string;
  setGridList?: Function;
  mappedRowsWithErrors?: any[];
  showHeaderWhenEmpty?: boolean;
  emptyState?: React.ReactNode;
  emptyStateAlign?: 'center' | 'top';
  filterOperators?: Record<string, 'and' | 'or'>;
  filterOrder?: string[];
};
export default React.memo(
  React.forwardRef(
    (
      {
        data: dataIn,
        onDoubleClick,
        columns,
        sortMode,
        additionalDragInfo,
        onRowSelected,
        updateSort,
        filter,
        filterByColumns,
        onFilterChanged,
        rowHeight = 37,
        readOnly,
        defaultMessage,
        gridList,
        setGridList,
        editMode,
        mappedRowsWithErrors,
        showHeaderWhenEmpty,
        emptyState,
        emptyStateAlign = 'center',
        filterOperators,
        filterOrder,
      }: TableProps,
      ref,
    ) => {
      const dispatch = useDispatch();
      const [sortFn, setSortFn] = useFunctionAsState(null);
      const [sortStuff, setSortStuff] = useState<any>({});
      const distinctData = useOriginalCopy(dataIn, _.isEqual);
      const prevDistinctData = usePrevious(distinctData);
      const prevFilter = usePrevious(filter);
      const prevSortFn = usePrevious(sortFn);
      const { filteredAndSortedData, setFilteredAndSortedData } = useFilteredAndSortedData();

      const [filteredData, setFilteredData] = useState<null | Array<any>>(null);
      const [sortedData, setSortedData] = useState<null | Array<any>>(null);
      const data = filteredData ? filteredData : sortedData ? sortedData : distinctData;
      const prevData = usePrevious(data, []) as Array<any>;
      const filterRowsByMappingErrors = useSelector(selectDataMapFailedRows);
      const isBundleTabPage = useSelector(selectBundleTabPage);
      const currentFolder = useSelector(selectCurrentFolder);
      const updateDocumentSortFeedback = useSelector(selectUpdateDocumentSortFeedback);

      const { file: currentFileParam, compositeKey: currentCompositeKeyParam } = useSelector(
        selectParams as any,
      ) as any;
      useEffect(() => {
        if (
          setGridList &&
          ((!equal(prevData, data) && (!currentFolder?.agreed || updateDocumentSortFeedback)) ||
            (currentFolder?.agreed && !equal(data, gridList)))
        ) {
          setGridList(data);
        }
      }, [data, prevData, setGridList, updateDocumentSortFeedback, currentFolder]);

      useEffect(() => {
        if (isEmpty(filter)) {
          setFilteredData(null);
          onFilterChanged && onFilterChanged(null, null);
          return;
        }

        const filteredDataTemp = filterData({
          data: sortedData || distinctData,
          filter,
          filterByColumns,
          isBundleTabPage,
          filterOperators,
          filterOrder,
        });

        setFilteredData(filteredDataTemp);
        onFilterChanged && onFilterChanged(filteredDataTemp, filter);
      }, [filter, sortedData, distinctData, filterOperators, filterOrder]);

      useEffect(() => {
        if (filterRowsByMappingErrors && mappedRowsWithErrors && mappedRowsWithErrors?.length > 0) {
          const onlyErrorRows = data?.filter((row) => mappedRowsWithErrors.includes(row.name));
          setFilteredData(onlyErrorRows as any);
          onFilterChanged && onFilterChanged(onlyErrorRows as any, filter);
        } else {
          const filteredDataTemp = filterData({
            data: sortedData || distinctData,
            filter,
            filterByColumns,
            isBundleTabPage,
            filterOperators,
            filterOrder,
          });
          setFilteredData(filteredDataTemp);
        }
      }, [filterRowsByMappingErrors, mappedRowsWithErrors]);

      useEffect(() => {
        if (prevSortFn === sortFn) return;

        if (!sortFn) {
          // preserve selected rows
          distinctData.forEach((item) => {
            const foundItem = data.find(
              ({ folderFileId, compositeKey, id }: any) =>
                (folderFileId || compositeKey || id) ===
                (item.folderFileId || item.compositeKey || item.id),
            );
            if (foundItem) item.selected = foundItem.selected;
            else item.selected = false;
          });
          setSortedData(null);
          if (!filter || isEmpty(filter)) {
            setFilteredAndSortedData(null);
          }
          // if filtered re-filter regular order
          if (filteredData) {
            const filteredDataTemp = filterData({
              data: distinctData,
              filter,
              filterByColumns,
              isBundleTabPage,
              filterOperators,
              filterOrder,
            });
            setFilteredData(filteredDataTemp);
          }
          return;
        }

        // sort data
        const sortedDataTemp = distinctData.slice().sort(sortFn);
        setSortedData(sortedDataTemp);
        // sort filtered data
        if (filteredData) {
          const filteredDataTemp = filteredData.slice().sort(sortFn);
          setFilteredData(filteredDataTemp);
        }
      }, [prevSortFn, sortFn, distinctData, filteredData, filter, filterByColumns, data]);

      // is new data
      useEffect(() => {
        if (prevDistinctData === distinctData) return;

        const isSorted = !!sortFn;
        const isFiltered = !!filter;

        // preserve selected rows only if incoming data doesn't already carry selection
        const incomingHasSelection = distinctData.some((row: any) => !!row.selected);
        if (!incomingHasSelection) {
          distinctData.forEach((item) => {
            const foundItem = prevData?.find(
              ({
                folderFileId,
                compositeKey,
                id,
              }: {
                folderFileId: string;
                compositeKey: string;
                id: string;
              }) =>
                (folderFileId || compositeKey || id) ===
                (item.folderFileId || item.compositeKey || item.id),
            );
            if (foundItem) item.selected = foundItem.selected;
            else item.selected = false;
          });
        }

        // if it's sorted we re-sort and it will get re-filtered automatically
        if (isSorted) {
          const resortedData = distinctData.slice().sort(sortFn);
          setSortedData(resortedData);
        }
        // if it's filtered then filter the new data
        else if (isFiltered) {
          const _filterData = filterData({
            data: distinctData,
            filter,
            filterByColumns,
            isBundleTabPage,
            filterOperators,
            filterOrder,
          });
          setFilteredData(_filterData);
        }
        // update row selected
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        onRowSelected &&
          onRowSelected(distinctData.filter(({ selected }: { selected: boolean }) => !!selected));
        return () => {
          if (distinctData.length === 0) {
            setSortFn(null);
            setSortStuff({});
          }
        };
      }, [
        prevDistinctData,
        distinctData,
        filter,
        filterByColumns,
        sortFn,
        onRowSelected,
        prevData,
        setSortFn,
      ]);

      const listRef = useRef();
      const containerRef = useRef<HTMLDivElement>(null);
      const [isFocused, setIsFocused] = useState(false);
      const [isPointerInside, setIsPointerInside] = useState(false);
      const lastInteractionInsideRef = useRef(false);
      const listApiRef = useRef<any>(null);
      const firstEditableCellRefs = useRef<React.RefObject<HTMLInputElement>[]>([]);
      const lastPublishedIdsRef = useRef<string[]>([]);

      useEffect(() => {
        if (firstEditableCellRefs.current.length !== data.length) {
          firstEditableCellRefs.current = data.map(
            (_, i) => firstEditableCellRefs.current[i] || React.createRef(),
          );
        }
      }, [data]);

      useEffect(() => {
        const getRowId = (row: any) => row.folderFileId || row.compositeKey || row.id;
        const toIds = (arr: any[] | null) => (arr ? arr.map(getRowId).filter(Boolean) : []);

        const outgoing =
          filteredData && filteredData.length > 0
            ? filteredData
            : !filteredData && sortedData && sortedData.length > 0
              ? sortedData
              : null;

        if (!outgoing) return;

        const outgoingIds = toIds(outgoing);
        const lastIds = lastPublishedIdsRef.current;

        const isSameOrder =
          outgoingIds.length === lastIds.length && outgoingIds.every((id, i) => id === lastIds[i]);

        if (!isSameOrder) {
          lastPublishedIdsRef.current = outgoingIds;
          setFilteredAndSortedData([...outgoing]);
        }
      }, [filteredData, sortedData, setFilteredAndSortedData]);

      // Initialize local sortedData from Redux to preserve order across remounts
      useEffect(() => {
        if (!filteredAndSortedData || !Array.isArray(filteredAndSortedData)) return;
        if (filter && !isEmpty(filter)) return;
        if (sortFn) return; // avoid interfering while an explicit sort is active
        if (filteredData) return; // avoid interfering while filter view is active

        const getRowId = (row: any) => row.folderFileId || row.compositeKey || row.id;

        const distinctIds = distinctData.map(getRowId).filter(Boolean);
        const incomingIds = (filteredAndSortedData as any[]).map(getRowId).filter(Boolean);

        if (
          incomingIds.length > 0 &&
          distinctIds.length === incomingIds.length &&
          incomingIds.every((id) => distinctIds.includes(id))
        ) {
          const rowById = new Map(distinctData.map((r: any) => [getRowId(r), r]));
          const ordered = incomingIds.map((id) => rowById.get(id)).filter(Boolean);

          const currentSortedIds = (sortedData || []).map(getRowId).filter(Boolean);
          const isSameOrder =
            currentSortedIds.length === incomingIds.length &&
            incomingIds.every((id, i) => id === currentSortedIds[i]);

          if (ordered.length === distinctData.length && !isSameOrder) {
            setSortedData(ordered as any);
          }
        }
      }, [filteredAndSortedData, distinctData, filter, sortedData, sortFn, filteredData]);

      const focusNextRow = useCallback(
        (currentRowIndex) => {
          if (currentRowIndex + 1 < data.length) {
            const nextRef = firstEditableCellRefs.current[currentRowIndex + 1];
            if (nextRef && nextRef.current) {
              nextRef.current.focus();
            }
          }
        },
        [data],
      );

      const onSortHandler = useCallback(
        ({ sortByColumn, sortDirection, onCustomSort }) => {
          setSortFn(getSortFunction(sortByColumn, sortDirection, onCustomSort));
          setSortStuff({ sortedColumn: sortByColumn, sortedDirection: sortDirection });
        },
        [setSortFn],
      );
      const [currentlyEditingRowIndex, setCurrentlyEditingRowIndex] = useState<number | null>(null);

      const folderSpecificProp = ['name', 'tab', 'startPage'];

      interface FileData {
        [key: string]: any;
      }

      const getFileData = (data: FileData, folderSpecificData: boolean): FileData => {
        return Object.entries(data).reduce((previousItem, [key, value]) => {
          if (folderSpecificData) {
            if (folderSpecificProp.includes(key)) {
              return { ...previousItem, [key]: value };
            } else {
              return previousItem;
            }
          } else {
            if (!folderSpecificProp.includes(key)) {
              return { ...previousItem, [key]: value };
            } else {
              return previousItem;
            }
          }
        }, {});
      };

      interface Data {
        [key: string]: any;
      }

      interface CleanData {
        [key: string]: any;
      }

      const getCleanData = (data: Data, originalData: Data): CleanData => {
        return Object.entries(data).reduce((cleanData, [key, value]) => {
          if (value !== originalData[key]) {
            return { ...cleanData, [key]: value };
          }
          return cleanData;
        }, {});
      };

      const handleSaveChanges = useCallback(
        (rowId, folderFileId, updatedData, originalData) => {
          // Extract and compare folder-specific data
          const folderSpecificData = getFileData(updatedData, true);
          const cleanFolderSpecificData = getCleanData(folderSpecificData, originalData);

          if (Object.keys(cleanFolderSpecificData).length > 0) {
            dispatch(updateDocument(cleanFolderSpecificData, rowId, folderFileId));
          }

          // Extract and compare file-level metadata
          const fileMetaData = getFileData(updatedData, false);
          const cleanFileMetaData = getCleanData(fileMetaData, originalData);

          if (Object.keys(cleanFileMetaData).length > 0) {
            dispatch(updateDocumentMeta(cleanFileMetaData, rowId));
          }
        },
        [dispatch],
      );

      const onRowClick = useCallback((rowIndex: number) => {
        if (currentlyEditingRowIndex !== null && currentlyEditingRowIndex !== rowIndex) {
          setCurrentlyEditingRowIndex(rowIndex);
        } else {
          setCurrentlyEditingRowIndex(rowIndex);
        }
      }, []);

      // Shared helpers for locating target rows based on route/hash
      const getPreferredTargetList = (
        filtered: Array<any> | null,
        sorted: Array<any> | null,
        distinct: Array<any>,
      ): Array<any> => {
        return (filtered && filtered.slice()) || (sorted && sorted.slice()) || distinct;
      };

      const createRowMatchPredicate = (
        currentCompositeKey?: string | null,
        currentFile?: string | null,
        hashId?: string | null,
      ) => {
        return (row: any) => {
          if (currentCompositeKey && row.compositeKey === currentCompositeKey) return true;
          if (currentFile && (row.id === currentFile || row.folderFileId === currentFile))
            return true;
          if (
            hashId &&
            (row.folderFileId === hashId || row.compositeKey === hashId || row.id === hashId)
          )
            return true;
          return false;
        };
      };

      // Highlight row when route changes to next/previous file
      useEffect(() => {
        const hasRouteSelection = !!currentFileParam || !!currentCompositeKeyParam;
        if (!hasRouteSelection) return;

        const shouldSelect = createRowMatchPredicate(
          currentCompositeKeyParam,
          currentFileParam,
          null,
        );

        const target = getPreferredTargetList(filteredData, sortedData, distinctData);

        // If selection state already matches, skip updates
        const selectionUnchanged = target.every(
          (row: any) => !!row.selected === !!shouldSelect(row),
        );
        if (selectionUnchanged) return;

        const updated = target.map((row: any) => ({ ...row, selected: shouldSelect(row) }));

        if (filteredData) setFilteredData(updated as any);
        else if (sortedData) setSortedData(updated as any);
        else {
          // Update in place for base data (keeps order intact)
          distinctData.forEach((row: any) => {
            row.selected = shouldSelect(row);
          });
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        onRowSelected && onRowSelected(updated.filter(({ selected }: any) => !!selected));
      }, [
        currentFileParam,
        currentCompositeKeyParam,
        filteredData,
        sortedData,
        distinctData,
        onRowSelected,
      ]);

      // Ensure the selected row is scrolled into view (no re-centering) after navigation/hash changes
      useEffect(() => {
        const currentUrlHashVal = window.location.hash;
        const hashFileId = currentUrlHashVal.split('#')[1];

        const targetList = getPreferredTargetList(filteredData, sortedData, distinctData);

        const shouldSelect = createRowMatchPredicate(
          currentCompositeKeyParam,
          currentFileParam,
          hashFileId,
        );

        const targetIndex = targetList.findIndex(shouldSelect);

        const performScrollToItem = (index: number) => {
          if (
            index < 0 ||
            !listApiRef.current ||
            typeof listApiRef.current.scrollToItem !== 'function'
          )
            return;
          try {
            // Use 'auto' so it only scrolls if item is outside the viewport;
            // avoids flicker from forced re-centering.
            listApiRef.current.scrollToItem(index, 'auto');
          } catch (e) {
            console.error(T.translate('generic.errorScrolling'), e);
          }
        };

        if (targetIndex >= 0) {
          // Single attempt; no retries or smooth scroll to reduce flicker
          performScrollToItem(targetIndex);
        }
      }, [currentFileParam, currentCompositeKeyParam, filteredData, sortedData, distinctData]);

      const isEditableElement = (el: any) => {
        if (!el || !(el instanceof HTMLElement)) return false;
        const tag = el.tagName && el.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || el.isContentEditable) return true;
        const input = el.closest && el.closest('input, textarea, [contenteditable="true"]');
        return !!input;
      };

      const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (!isFocused) return;
          if (!(e.metaKey || e.ctrlKey)) return;
          const key = (e.key || '').toLowerCase();
          if (key !== 'a') return;
          if (isEditableElement(e.target)) return; // allow native select-all in inputs
          e.preventDefault();
          e.stopPropagation();
          // call into row imperative handle to select all
          const anyRef: any = ref as any;
          if (anyRef && anyRef.current && typeof anyRef.current.selectAllRows === 'function') {
            anyRef.current.selectAllRows();
          } else {
            // Fallback: mark all current rows selected when ref is not wired yet
            const rows = data || [];
            rows.forEach((r: any) => (r.selected = true));
            onRowSelected && onRowSelected(rows.slice());
          }
        },
        [isFocused, ref, onRowSelected, data],
      );

      // Ensure Cmd/Ctrl+A is intercepted even if React onKeyDown doesn't fire
      useEffect(() => {
        const handler = (e: KeyboardEvent) => {
          if (!(e.metaKey || e.ctrlKey)) return;
          const key = (e.key || '').toLowerCase();
          if (key !== 'a') return;
          const target = e.target as HTMLElement | null;
          const container = containerRef.current;
          if (!container) return;
          const inside = !!(target && container.contains(target));
          const activeInside = !!(
            document.activeElement && container.contains(document.activeElement as Node)
          );
          if (
            !(
              isFocused ||
              isPointerInside ||
              inside ||
              activeInside ||
              lastInteractionInsideRef.current
            )
          )
            return;
          if (isEditableElement(target) || isEditableElement(document.activeElement)) return;
          e.preventDefault();
          e.stopPropagation();
          const anyRef: any = ref as any;
          if (anyRef && anyRef.current && typeof anyRef.current.selectAllRows === 'function') {
            anyRef.current.selectAllRows();
          } else {
            const rows = data || [];
            rows.forEach((r: any) => (r.selected = true));
            onRowSelected && onRowSelected(rows.slice());
          }
        };
        window.addEventListener('keydown', handler, true);
        return () => window.removeEventListener('keydown', handler, true);
      }, [ref, onRowSelected, data, isFocused, isPointerInside]);
      return (
        <div
          className="common-table"
          ref={containerRef}
          tabIndex={0}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            // Blur may fire when focusing inner inputs; keep focus state if still inside
            const next = e.relatedTarget as HTMLElement | null;
            if (containerRef.current && next && containerRef.current.contains(next)) return;
            setIsFocused(false);
            lastInteractionInsideRef.current = false;
          }}
          onMouseEnter={() => {
            setIsPointerInside(true);
            lastInteractionInsideRef.current = true;
          }}
          onMouseLeave={() => setIsPointerInside(false)}
          onMouseDownCapture={(e) => {
            // focus container when interacting with table area, but do not steal from inputs
            if (isEditableElement(e.target)) return;
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            containerRef.current && containerRef.current.focus();
            lastInteractionInsideRef.current = true;
          }}
          onKeyDown={handleKeyDown}
        >
          {
            //needed so that it doesn't go outside card boundaries
            <div style={{ height: '100%' }}>
              {data && data.length > 0 ? (
                <ScrollDataContext.Provider value={{ dataLength: data.length }}>
                  <AutoSizer>
                    {({ height, width }: { height: number; width: number }) => (
                      <DroppableBodyContainerProvider
                        updateSort={updateSort}
                        columns={columns}
                        data={data}
                        sortMode={sortMode}
                        forwardedRef={listRef}
                        onSortHandler={onSortHandler}
                        sortedColumn={sortStuff.sortedColumn}
                        sortedDirection={sortStuff.sortedDirection}
                      >
                        <List
                          ref={listApiRef}
                          outerElementType={CustomScrollbarsVirtualList}
                          innerElementType={DroppableBody}
                          outerRef={listRef}
                          height={height}
                          width={width}
                          itemCount={data.length}
                          itemSize={rowHeight}
                          itemData={{
                            data: data,
                            onDoubleClick,
                            columns,
                            sortMode,
                            editMode,
                            additionalDragInfo,
                            onRowSelected,
                            readOnly,
                            forwardedRef: ref,
                            focusNextRow,
                            handleSaveChanges,
                            handleCancelChanges: () => {}, // TODO get rid of later
                            onRowClick,
                            currentlyEditingRowIndex,
                            firstEditableCellRefs: firstEditableCellRefs.current,
                            mappedRowsWithErrors,
                          }}
                        >
                          {ListRow}
                        </List>
                      </DroppableBodyContainerProvider>
                    )}
                  </AutoSizer>
                </ScrollDataContext.Provider>
              ) : (
                <>
                  {columns && columns.length > 0 && (showHeaderWhenEmpty || emptyState) ? (
                    <div className="header-and-empty">
                      <div className="header-scroll">
                        <BootstrapTable className={showHeaderWhenEmpty ? undefined : 'sticky'}>
                          <HeaderRow
                            columns={columns}
                            onSort={onSortHandler}
                            sortedColumn={sortStuff.sortedColumn}
                            sortedDirection={sortStuff.sortedDirection}
                          />
                        </BootstrapTable>
                      </div>
                      <div
                        className={`empty-state-container ${emptyStateAlign === 'top' ? 'align-top' : 'align-center'}`}
                      >
                        {emptyState ||
                          (filteredData && (
                            <NoDataMessage message={T.translate('case.filterNoMatch')} />
                          )) ||
                          (defaultMessage && <NoDataMessage message={defaultMessage} />)}
                      </div>
                    </div>
                  ) : (
                    <>
                      {filteredData && (
                        <NoDataMessage message={T.translate('case.filterNoMatch')} />
                      )}
                      {!filteredData && defaultMessage && (
                        <NoDataMessage message={defaultMessage} />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          }
        </div>
      );
    },
  ),
);
